package com.oasisfeng.invaluable.social

import com.oasisfeng.invaluable.Quantity
import com.oasisfeng.invaluable.data.project
import com.oasisfeng.invaluable.event.DurableCursor
import com.oasisfeng.invaluable.event.DurableWrite
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update

/**
 * Front-end-agnostic façade that ties a [SocialNode] to persistence and replication.
 *
 * Both the Web (Compose HTML) and the TUI drive this same client: author through [perform]/[runLine], read the world
 * through [board]/[context], and wire opened transport sessions through [sync]. Every state advance — local authoring
 * or a peer's event — writes what it added to the durable copy and re-renders, so a reload restores the local world and
 * a connected peer stays in sync.
 */

class SocialClient(
    val node: SocialNode,
    /**
     * Put one [DurableWrite] into the node's durable copy (a file, localStorage, a row) — **true once the copy holds it**.
     *
     * The answer is what keeps an incremental write honest. A whole snapshot repaired a failed write for free, since the
     * next one carried everything again; an append that was dropped is a hole nothing later fills. Answering false (or
     * throwing) leaves this client's record of the copy where it was, so the missing lines ride along with the next write.
     */
    private val persist: (DurableWrite) -> Boolean,
    /** Re-render the front-end. */
    private val render: () -> Unit,
    private val log: (String) -> Unit = {},
    /**
     * Count of peer links open right now, or null when the transport is not running (e.g. a one-shot CLI read). Lives
     * in the js transport layer (`SessionTracker`); injected here so [context] can state this one live, local fact
     * without commonMain depending on the transport.
     */
    private val presence: () -> Int? = { null },
    /**
     * Coalesces a burst of peer-advance callbacks into a single persist + render. A late joiner's catch-up delivers a
     * whole chain suffix as thousands of back-to-back events; persisting after each one re-encodes the entire (growing)
     * store every time — O(events²) — which makes even a one-post catch-up take tens of seconds. A host with an event
     * loop passes a debouncer (e.g. setTimeout that resets each call) so a settled burst persists exactly once; the
     * default runs the flush immediately, preserving the synchronous behaviour tests and a one-shot CLI rely on.
     */
    private val coalesce: (flush: () -> Unit) -> Unit = { it() },
    /**
     * Coalesces the *persisting* half of a peer advance, which has a different requirement from the visible half.
     *
     * Bumping the change version and rendering are cheap and wanted promptly, so they belong on a short debounce.
     * Writing the store still encodes everything that arrived since the last write, so during a catch-up burst its cost
     * tracks the burst; scheduling it on the same "settled for 30ms" rule would make its frequency a function of how
     * long it blocks rather than of how much changed. Hosts pass `costAwareCoalescer()` here; the default keeps both
     * halves on [coalesce], preserving the synchronous behaviour tests and a one-shot CLI rely on.
     */
    private val coalescePersist: (flush: () -> Unit) -> Unit = coalesce,
    /**
     * Persist the head projections of the actor's private posts (see [postPrivate]) as one base64url-per-line blob.
     * Injected like [persist] so the store stays transport- and filesystem-agnostic; the default no-op keeps them in
     * memory (tests, ephemeral hosts). The private posts' full content lives in the ordinary [persist]ed store; this
     * sidecar only re-marks *which* of those commitments stay withheld from peers on the next boot (see [restorePrivate]).
     */
    private val persistPrivate: (String) -> Unit = {},
    /**
     * Persist the content-keys this node has been disclosed (see [SocialNode.encodeSeals]) as one base64url pair per
     * line. Injected like [persistPrivate]; the default no-op keeps them in memory (tests, ephemeral hosts). These are
     * device-local secrets, so a host wires this to the same private location as the identity key — never the wire.
     * Without it a disclosed reader keeps custodying the sealed ciphertext but loses the key on restart, going dark
     * again until re-disclosed.
     */
    private val persistSeals: (String) -> Unit = {},
    /**
     * Ordered transport catch-up barrier from [PeerSync]. Hosts that expose actionable remote reads use this instead of
     * treating a connected socket as proof that the local observation is current.
     */
    onCaughtUp: (com.oasisfeng.invaluable.p2p.core.P2pSession) -> Unit = {},
) {
    /**
     * The local actor's private posts are not a separate plane — they are ordinary commitments ([SocialNode.postPrivate])
     * whose committed content is **withheld** from replication ([L16] / roadmap §7.1 selective reveal). This node's own
     * view resolves them like any post (their content is in the store), so a private post is a real post only the author
     * can see; peers see an attributable missing observation until it is shared. Sharing — [publish] (to everyone) or
     * [recommend] (to one actor) — reveals the withheld content, at which point the post and its weight become ordinary
     * public facts. Backing your own private post ([backPrivate]) is just a withheld back commitment that raises its
     * weight in your frame. The withheld content never reaches a peer, so it never enters anyone else's value field.
     */
    // Authoring propagates through node.onLocalEvents (wired by PeerSync), and a received event relays itself onward.
    // Local authoring persists synchronously in `perform`; only the bursty remote-advance path is coalesced (any events
    // dropped before a deferred flush are simply re-synced from a peer, since replication is grow-only).
    // A remote advance can change the disclosed-key set too: a wrap arriving after its ciphertext fires
    // onCapabilityLearned (routed here by PeerSync), and a ciphertext arriving after its wrap promotes a pending key —
    // both surface as an advance. So the coalesced flush persists the seal sidecar alongside the store.
    val sync: PeerSync = PeerSync(
        node,
        onAdvanced = {
            coalesce { bumpChangeVersion(); render() }
            coalescePersist { persistState(); persistSealKeys() }
        },
        log = log,
        onCaughtUp = onCaughtUp,
    )

    private val _changeVersion = MutableStateFlow(0L)

    /**
     * Monotonically increasing counter, bumped alongside every [render] call (same call sites, same conditions) —
     * a non-polling alternative for a caller that wants to suspend until "something changed" instead of polling on a
     * fixed cadence (e.g. [AgentTools.waitInboxUpdate]). Race-free usage: capture [changeVersion]'s current value
     * *before* reading whatever derived state you actually care about, and only if that state doesn't already show
     * what you're waiting for, suspend on `changeVersion.first { it != baseline }` — a [StateFlow] collector's first
     * emission is always the flow's *current* value at the moment collection begins, so a change landing between
     * capturing the baseline and starting to collect is still seen immediately, not missed.
     *
     * On the remote-sync path this bumps *inside* [coalesce], not once per individual inbound event: a late joiner's
     * catch-up can replay thousands of back-to-back events, and a waiter that reacted to every single one of those
     * bumps (recomputing its own derived state each time) would turn an already-heavy catch-up burst into much more
     * work happening concurrently on the same single JS thread — the opposite of the fixed-cadence poll this replaced,
     * which naturally coalesced a burst for free by only ever checking once per tick regardless of how many changes
     * landed in it. Coalescing here restores that property: a waiter wakes at most once (well under the coalescer's
     * debounce window) per *settled* burst, not once per event within it.
     */
    val changeVersion: StateFlow<Long> = _changeVersion.asStateFlow()

    private fun bumpChangeVersion() { _changeVersion.update { it + 1 } }

    /**
     * How much of the node's state the durable copy already holds; every write starts from here and leaves it further on.
     *
     * Only two things may move it: a write the copy took, and a restore *of that same copy* — the two moments this
     * client actually knows where the copy stands. It starts [DurableCursor.Empty], so a host that never restores (a
     * fresh node, a missing file) writes the whole state once before it ever appends.
     */
    private var durable = DurableCursor.Empty

    /**
     * Restore previously persisted state at boot — our own writing, replayed without re-judging it.
     *
     * [serialized] must be the current contents of the very copy [persist] writes to, since afterwards that copy is
     * taken to hold everything the node does. A host that merges a *foreign* blob into a live node (the browser's
     * backup import) has to follow it with [persistWholeState]: the copy then holds neither what it had nor what the
     * node has, and only a whole write can say what it should.
     */
    fun restore(serialized: String?) {
        if (serialized == null) return
        node.restoreLines(serialized.lineSequence().asIterable())
        durable = node.cursorAfterRestore()
    }

    /**
     * [restore], in slices, yielding the host thread between them and reporting progress.
     *
     * Restoring a whole log is seconds of work — every batch is re-staged and every preimage re-projected — and on a
     * single-threaded host doing it in one call means the UI is simply gone for that whole time (the browser puts up
     * its "page unresponsive" dialog). Slicing changes nothing about what is restored:
     * [ReplicatedEventNode.restoreLines] keeps its batch stager across calls, so feeding the same lines in pieces
     * stages exactly the same batches; only the thread gets to breathe between them.
     *
     * [onProgress] is called after each slice with the lines consumed so far and the total, for a boot indicator.
     *
     * The copy is only marked as holding all this at the end, not per slice — counting the store is a walk of it, and
     * doing that between every sixteen lines would cost more than the restore. A peer event landing between two slices
     * is therefore counted as already written; like any event dropped before a deferred flush, replication is grow-only
     * and a peer re-pushes it.
     */
    suspend fun restoreInSlices(serialized: String?, onProgress: (restored: Int, total: Int) -> Unit = { _, _ -> }) {
        val lines = serialized?.lines() ?: return
        var from = 0
        while (from < lines.size) {
            val to = minOf(from + RESTORE_SLICE_LINES, lines.size)
            node.restoreLines(lines.subList(from, to))
            from = to
            onProgress(from, lines.size)
            delay(1)   // a macrotask boundary: a microtask yield would still deny the host a paint
        }
        durable = node.cursorAfterRestore()
    }

    /** Re-mark which restored commitments stay withheld from peers (their content is already in the restored store). */
    fun restorePrivate(serialized: String?) {
        if (serialized != null) node.restorePrivateHeads(serialized.lineSequence().asIterable())
    }

    /** Restore previously disclosed content-keys at boot (call after [restore], so the matching ciphertext is present). */
    fun restoreSeals(serialized: String?) {
        if (serialized != null) node.restoreSeals(serialized.lineSequence().asIterable())
    }

    /**
     * Remember a private post: author it as an ordinary commitment with positive [cost], but withhold its content from
     * peers (selective reveal). Returns the post's [ref] — stable, content-addressed, so it survives a cold restart and
     * names the post for [backPrivate] / [publish] / [recommend]. Peers see only a missing observation until it is shared.
     */
    fun postPrivate(text: String, cost: Quantity = Quantity.ONE): SocialRef {
        require(cost.isPositive()) { "a private post's stake must be a positive amount" }
        val post = node.postPrivate(text, cost)
        persistState(); persistPrivateHeads(); bumpChangeVersion(); render()
        return socialRefOf(post.link, node.projector)
    }

    /**
     * Author a *sealed* post (Milestone 13): like [postPrivate] the plaintext is not gossiped, but the content is sealed
     * and its ciphertext replicated so peers can custody it without reading. Disclose it to an actor with `recommend`,
     * which rides a directed key-wrap to them. Returns the post's stable [ref].
     */
    fun postSealed(text: String, cost: Quantity = Quantity.ONE): SocialRef {
        require(cost.isPositive()) { "a sealed post's stake must be a positive amount" }
        val post = node.postSealed(text, cost)
        persistState(); persistSealKeys(); bumpChangeVersion(); render()   // the author's own content-key must survive a restart too
        return socialRefOf(post.link, node.projector)
    }

    /**
     * Back one of the local actor's own private posts by [ref], raising its weight by [amount] with a withheld back
     * commitment; false if [ref] is not one of your private posts. Like the post itself, the back is never gossiped until
     * the post is shared, so it stays in your own frame only.
     */
    fun backPrivate(ref: SocialRef, amount: Quantity): Boolean {
        require(amount.isPositive()) { "backing a private post must raise its weight by a positive amount" }
        if (!isPrivatePost(ref)) return false
        val post = node.board().post(ref) ?: return false
        node.backPrivate(post, amount)
        persistState(); persistPrivateHeads(); bumpChangeVersion(); render()
        return true
    }

    /**
     * Reveal a private post named by [ref] (and any private backs on it): gossip its withheld content so peers resolve it
     * into a full public post with its weight. False if [ref] is not one of your private posts.
     */
    fun reveal(ref: SocialRef): Boolean {
        val post = node.board().post(ref) ?: return false
        if (!node.reveal(node.projector.project(post.link))) return false
        persistState(); persistPrivateHeads(); bumpChangeVersion(); render()
        return true
    }

    /** The refs of the local actor's current private posts (withheld from peers) — for rendering and to route [back]. */
    fun privatePostRefs(): Set<SocialRef> = node.privatePostRefProjections().mapTo(mutableSetOf()) { socialRefOf(it) }

    /** Whether [ref] names one of the local actor's own private (unrevealed) posts. */
    fun isPrivatePost(ref: SocialRef): Boolean = ref in privatePostRefs()

    private fun persistPrivateHeads() = persistPrivate(node.encodePrivateHeads().joinToString("\n"))

    private fun persistSealKeys() = persistSeals(node.encodeSeals().joinToString("\n"))

    /** Parse and perform one action line; returns null when the line failed to parse. */
    fun runLine(line: String): Pair<ParsedAction, ActionOutcome?> = when (val parsed = ActionGrammar.parse(line)) {
        is ParsedAction.Error -> parsed to null
        is ParsedAction.Ok -> parsed to perform(parsed.action)
    }

    /** Perform a resolved action against the current board, then persist and render. (Peers are fed via onLocalEvents.) */
    fun perform(action: SocialAction): ActionOutcome {
        val outcome = node.perform(action, node.board())
        if (outcome is ActionOutcome.Done) { persistState(); bumpChangeVersion(); render() }
        return outcome
    }

    fun board(): SocialBoard = node.board()

    /** Make [text] the local actor's top-ranked profile appearance (out-bidding others by [amount]), then persist. */
    fun ensureLocalProfile(text: String, amount: Quantity = Quantity.ONE): Boolean
    = node.ensureLocalProfile(text, amount).also { changed ->
        if (changed) { persistState(); bumpChangeVersion(); render() }
    }
    fun context(note: String? = null, continuing: Boolean = false): String =
        ContextRenderer.render(node.board(), note, continuing, livePeers = presence(), privateRefs = privatePostRefs())

    /**
     * Write down whatever the node has gained since the durable copy was last brought current.
     *
     * Public for a host that advances the node itself rather than through this client — the TUI takes a wire line pasted
     * into its console straight into [node] — since only this client knows where the copy stands.
     */
    fun persistState(): Boolean = persistFrom(durable)

    /**
     * Synchronously bring remote semantic state and disclosed keys to durable storage.
     *
     * Transport-local acknowledgements such as an opaque relay cursor may advance only after this returns true. A false
     * result or exception leaves that transport watermark behind, so a restart safely replays the same capsule.
     */
    fun confirmRemoteDurability(): Boolean {
        if (! persistState()) return false
        persistSealKeys()
        return true
    }

    /**
     * Replace the durable copy with everything the node holds.
     *
     * For a host that changed the node behind this client's back and knows the copy no longer matches — the browser
     * re-decodes a pre-batch blob straight into the node and re-saves it in the current wire form. An append cannot say
     * that: it only ever extends, and what is on the copy there is the very thing that has to go.
     */
    fun persistWholeState(): Boolean = persistFrom(DurableCursor.Empty)

    private fun persistFrom(cursor: DurableCursor): Boolean {
        val write = node.encodeSince(cursor)
        if (write.vacuous) return true
        if (! persist(write)) return false
        durable = write.cursor
        return true
    }

    private companion object {
        /**
         * Lines per [restoreInSlices] slice. The real floor is one batch — a batch does all its work when its closing
         * line lands — so this only bounds how many batches share a task; a couple of them keeps a slice short enough
         * to stay out of an interaction's way, at the cost of one host tick each.
         */
        const val RESTORE_SLICE_LINES = 16
    }
}
